import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

/**
 * Iter 182 — Terms & Conditions.
 *
 * Linked from Profile → Notifications. Focused on SMS and email
 * messaging preferences (what the member opts into when they turn on
 * our toggles, how to opt out, carrier/data-rate language, frequency,
 * HELP/STOP keywords).
 */
export default function Terms() {
    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
            <Link to="/profile" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4" data-testid="terms-back">
                <ArrowLeft className="h-4 w-4" /> Back to profile
            </Link>
            <div className="flex items-center gap-3 mb-2">
                <FileText className="h-7 w-7 text-primary" />
                <h1 className="font-heading text-3xl sm:text-4xl font-black">Terms & Conditions</h1>
            </div>
            <p className="text-muted-foreground">Last updated: October 2026</p>

            <div className="prose prose-slate max-w-none mt-8 space-y-6 text-sm leading-relaxed">
                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">1. Overview</h2>
                    <p>
                        These Terms govern the SMS messages, email notifications, and push
                        alerts you receive from Alpha Omega Phi Military Fraternity &amp;
                        Sorority, Inc. (&ldquo;AOP&rdquo;, &ldquo;we&rdquo;, or
                        &ldquo;us&rdquo;) through the member portal.
                    </p>
                </section>

                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">2. SMS Message Program</h2>
                    <p>
                        By enabling the SMS toggles in your Profile → Notifications tab,
                        you authorize AOP to send recurring text messages to the mobile
                        number on file. Messages may include chat notifications, event
                        reminders, dues notices, and chapter announcements.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><b>Frequency:</b> variable — typically fewer than 10 messages per week.</li>
                        <li><b>Cost:</b> Message and data rates may apply. Carriers are not liable for delayed or undelivered messages.</li>
                        <li><b>Opt-out:</b> Reply <b>STOP</b> to any text to opt out of further SMS. Reply <b>HELP</b> for assistance.</li>
                        <li><b>Opt-in via portal:</b> Toggle individual SMS categories off at any time from Profile → Notifications.</li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">3. Email Preferences</h2>
                    <p>
                        You consent to receive email from AOP when you create an account
                        or opt in to a category in your email preferences. We send
                        account-critical email (password resets, security alerts, RSVP
                        receipts) regardless of marketing preferences — those messages are
                        required to run your account.
                    </p>
                    <p>
                        Every bulk/newsletter email contains a one-click unsubscribe link
                        in the footer and honors the <code>List-Unsubscribe</code> header.
                        You can also manage each category individually from Profile →
                        Notifications → Email preferences.
                    </p>
                </section>

                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">4. Push Notifications</h2>
                    <p>
                        Push notifications are delivered through OneSignal and your
                        browser or OS. You can disable them at any time via the Disable
                        button on the Push notifications card in your profile, or in your
                        browser/OS settings. Disabling push removes your device token
                        from our records within 24 hours.
                    </p>
                </section>

                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">5. Changes to Preferences</h2>
                    <p>
                        We honor opt-out requests immediately. Residual messages already
                        queued may still deliver briefly after a change. If a message
                        continues to arrive after 48 hours, email <a href="mailto:support@alphaomegaphi.org" className="text-primary underline">support@alphaomegaphi.org</a>.
                    </p>
                </section>

                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">6. Governing Law</h2>
                    <p>
                        These Terms are governed by the laws of the United States of
                        America. By continuing to use the AOP member portal you agree to
                        these Terms. We reserve the right to update them from time to
                        time; the &ldquo;Last updated&rdquo; date at the top of this page
                        will always reflect the current version.
                    </p>
                </section>
            </div>
        </div>
    );
}
